import { useEffect, useState } from 'react';
import { api } from '../api';
import useHelpTarget from '../hooks/useHelpTarget';

/**
 * Consent-gated modal for the upgrade-tier transcription feature.
 *
 * Provider-agnostic: lists every registered provider via
 * /processing/upgrade-providers and renders the right cost + warning
 * for the selected one. The feature name in the UI ("Full Transcript")
 * stays constant regardless of which vendor is selected.
 *
 * Two scopes:
 *   scope="document" — single doc upgrade
 *   scope="case"     — bulk upgrade for every audio/video doc in a case
 *
 * Props:
 *   open       — boolean
 *   onClose    — callback()
 *   scope      — "document" | "case"
 *   targetId   — document_id or case_id depending on scope
 *   onComplete — callback() fires after successful submission
 */
export default function UpgradeTranscriptModal({ open, onClose, scope, targetId, onComplete }) {
  const [providers, setProviders] = useState(null);
  const [providerId, setProviderId] = useState('assemblyai');
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  // Help anchor — right-click the consent warning or hit ⌘/
  // while inside it. Opens the transcripts article scoped.
  // Callback ref pattern handles the warning's conditional mount.
  const dataBoundaryHelpRef = useHelpTarget({
    slug: 'transcripts-and-speaker-labels',
    label: 'Data boundary acknowledgement',
    question: 'Why does this transcript option require consent?',
  });

  // Load registered providers once on mount.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/processing/upgrade-providers')
      .then(d => {
        if (cancelled) return;
        const list = d?.providers || [];
        setProviders(list);
        if (list.length && !list.find(p => p.id === providerId)) {
          setProviderId(list[0].id);
        }
      })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [open]);

  // Fetch the cost estimate when provider or target changes.
  useEffect(() => {
    if (!open || !targetId || !providerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAcknowledged(false);

    const url = scope === 'case'
      ? `/processing/case/${targetId}/transcribe-upgrade?estimate=true&provider=${providerId}`
      : `/processing/documents/${targetId}/transcribe-upgrade?estimate=true&provider=${providerId}`;

    api.post(url)
      .then(d => { if (!cancelled) setEstimate(d); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, scope, targetId, providerId]);

  if (!open) return null;

  const provider = estimate?.provider
    || providers?.find(p => p.id === providerId)
    || null;

  const dollars = ((estimate?.estimated_cost_cents || 0) / 100).toFixed(2);
  const hasWarning = !!provider?.data_boundary_warning;
  const needsAcknowledgement = hasWarning;
  const canSubmit = !loading && !submitting && estimate && (!needsAcknowledgement || acknowledged);

  function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const url = scope === 'case'
      ? `/processing/case/${targetId}/transcribe-upgrade?estimate=false&provider=${providerId}`
      : `/processing/documents/${targetId}/transcribe-upgrade?estimate=false&provider=${providerId}`;
    api.post(url)
      .then(d => {
        // The endpoint dispatches a background job and returns
        // immediately with mode="background" + job_id. The
        // BackgroundJobsBanner picks up progress; the dropdown's
        // upgrade_job poll auto-refreshes the chat-log on completion.
        onComplete?.(d);
        onClose?.();
      })
      .catch(err => setError(err.message))
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="upg-overlay" onClick={onClose}>
      <div className="upg-modal" onClick={e => e.stopPropagation()}>
        <div className="upg-header">
          <h3>Generate Full Transcript</h3>
          <button className="upg-close" onClick={onClose}>✕</button>
        </div>

        <div className="upg-body">
          <p className="upg-intro">
            {scope === 'case'
              ? 'Generate a complete speaker-labeled transcript for every audio and video document in this case. Includes chapters and entity detection.'
              : 'Generate a complete speaker-labeled transcript with chapters and entity detection. Adds to the existing transcript without replacing it.'}
          </p>

          {/* Boundary-aware provider list (AW plan D9): the API only offers
              out-of-boundary providers to orgs that opted in. */}
          {providers && providers.length === 0 && (
            <p className="upg-error">
              No upgrade transcription providers are enabled for your organization. Providers outside the
              CJIS-attested environment (e.g. AssemblyAI) require an organization-level opt-in — contact ChargeStack support.
            </p>
          )}

          {/* Provider picker — only render if more than one is available. */}
          {providers && providers.length > 1 && (
            <div className="upg-provider">
              <label>Transcription provider</label>
              <select
                value={providerId}
                onChange={e => setProviderId(e.target.value)}
                disabled={submitting}
              >
                {providers.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.display_name}
                    {p.stays_in_google_workloads ? ' · stays in Google Workloads' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading && <div className="upg-loading">Calculating cost…</div>}

          {error && <div className="upg-error">⚠ {error}</div>}

          {!loading && estimate && (
            <>
              {/* Cost block */}
              <div className="upg-cost">
                {scope === 'case' ? (
                  <>
                    <div className="upg-cost-line">
                      <span className="upg-cost-label">Documents to transcribe</span>
                      <span className="upg-cost-value">{estimate.candidate_count}</span>
                    </div>
                    {estimate.skipped_count > 0 && (
                      <div className="upg-cost-line upg-cost-line--muted">
                        <span className="upg-cost-label">Already done (skipped)</span>
                        <span className="upg-cost-value">{estimate.skipped_count}</span>
                      </div>
                    )}
                    <div className="upg-cost-line">
                      <span className="upg-cost-label">Total audio</span>
                      <span className="upg-cost-value">{Math.round((estimate.total_duration_seconds || 0) / 60)} min</span>
                    </div>
                  </>
                ) : (
                  <div className="upg-cost-line">
                    <span className="upg-cost-label">Audio length</span>
                    <span className="upg-cost-value">{Math.round((estimate.duration_seconds || 0) / 60)} min</span>
                  </div>
                )}
                <div className="upg-cost-line upg-cost-line--total">
                  <span className="upg-cost-label">Estimated cost</span>
                  <span className="upg-cost-value">${dollars}</span>
                </div>
              </div>

              {/* Data-boundary warning — only when the provider's warning is non-null. */}
              {hasWarning && (
                <div className="upg-warning" ref={dataBoundaryHelpRef}>
                  <div className="upg-warning-title">⚠ Data leaves the CJIS-attested environment (Google Assured Workloads)</div>
                  <p>{provider.data_boundary_warning}</p>
                  <label className="upg-acknowledge">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={e => setAcknowledged(e.target.checked)}
                    />
                    {scope === 'case'
                      ? 'I confirm every audio/video file in this case is approved for processing outside Google Workloads.'
                      : 'I confirm this file is approved for processing outside Google Workloads.'}
                  </label>
                </div>
              )}

              {/* Provider attribution — small footer text */}
              <p className="upg-attribution">
                Powered by {provider?.display_name || 'the configured provider'}
                {provider?.speech_model ? ` (${provider.speech_model})` : ''}
                {provider?.stays_in_google_workloads ? ' · stays inside Google Workloads' : ''}
              </p>

              {estimate.already_complete && scope === 'document' && (
                <div className="upg-info">
                  ℹ This document already has a full transcript. Running again will replace it.
                </div>
              )}
            </>
          )}
        </div>

        <div className="upg-footer">
          <button className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting
              ? 'Queuing…'
              : (scope === 'case' ? `Queue ${estimate?.candidate_count || 0} transcripts` : 'Queue Transcript')}
          </button>
        </div>
      </div>
    </div>
  );
}
